// One market or all of them, as a pill: at the end of the Storefront tab row, and in the canvas
// toolbar, where it picks the market whose words the preview shows.
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy from '@/i18n/ns/storefront';
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { WorkspaceIcon } from '../icons';
import type { StoreMarket } from './api';

export function MarketFilter({
  markets,
  value,
  onChange,
  withAll = true,
}: {
  markets: StoreMarket[];
  value: string;
  onChange: (code: string) => void;
  /** The canvas previews one market at a time: no "All markets". */
  withAll?: boolean;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const current = markets.find((entry) => entry.code === value) ?? null;
  return (
    <Menu>
      <MenuTrigger
        className="sf-market-pill"
        // The label carries what the pill shows, so the chosen market is announced (WCAG 2.5.3).
        aria-label={`${copy.marketLabel}: ${current?.name ?? copy.allMarkets}`}
        data-testid="sf-market-filter"
        data-set={current && withAll ? 'true' : undefined}
      >
        {current && <span className="sf-cc">{current.code.toUpperCase()}</span>}
        {current?.name ?? copy.allMarkets}
        <WorkspaceIcon name="chevron" size={14} />
      </MenuTrigger>
      <MenuContent label={copy.marketLabel} align="end" className="sf-store-menu">
        <MenuRadioGroup value={value} onValueChange={onChange}>
          {withAll && (
            <>
              <MenuRadioItem
                value="all"
                description={formatCopy(copy.allMarketsNote, { count: String(markets.length) })}
              >
                {copy.allMarkets}
              </MenuRadioItem>
              <MenuSeparator />
            </>
          )}
          {markets.map((entry) => (
            <MenuRadioItem
              key={entry.code}
              value={entry.code}
              description={`${entry.site} · ${entry.currency}`}
            >
              {entry.name}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}
